// Copyright ©️ 2026 eVolve MEP, LLC

import type { ALL_MODULE_ROUTES } from 'app/ModuleRoutes';
import { WelcomeScreenImages } from 'assets/images/images';
import type { PageTitle } from 'components/Mantine/Navigation/types';

/**
 * The majority of welcome screens are used to give the user info about
 * a page they are visiting for the first time. At the moment, all of these
 * pages use an identical structure: Image, Title, and Description. They are
 * then rendered via `PageSpecificWelcomeModal`.
 *
 * @remarks Keyed by route, because the route is also the `featureName` used to remember whether
 * the modal has been shown. Changing a route will cause that welcome modal to reappear for users.
 */
export const pageSpecificWelcomeScreens = {
  '/work-orders': {
    title: 'Work Orders',
    image: WelcomeScreenImages.WorkOrders,
    description:
      'Work Orders organize prefab work from planning through completion. They group the work items your shop needs to build with the tasks, assignees, dates, attachments, and other information needed to get the work done.',
  },
  '/shipping-orders': {
    title: 'Shipping Orders',
    image: WelcomeScreenImages.ShippingOrders,
    description:
      'Shipping orders track deliveries to the field. Bundle your shippable items into a shipment to manage delivery status, dates, and packing slips to help ensure your shipments get to the right place at the right time.',
  },
  '/tasks': {
    title: 'Tasks',
    image: WelcomeScreenImages.Tasks,
    description:
      'Tasks are the individual steps required to complete work items. Assign users to tasks, track progress and time, and view tasks by individual work station, project, or across the whole shop.',
  },
  '/bill-of-materials': {
    title: 'Bill of Materials',
    image: WelcomeScreenImages.BillOfMaterials,
    description:
      'Bill of Materials displays the material required across your Work Orders. Review item details and quantities from Shop, your catalog, and Revit so your team knows what needs to be on hand to complete the work.',
  },
  '/time-tracking': {
    title: 'Time Tracking',
    image: WelcomeScreenImages.TimeTracking,
    description:
      'Time Tracking gives you visibility into the labor recorded against EVOLVE Shop tasks. Review and add time by employee, task, project, and Work Order to understand where your shop’s time is being spent.',
  },
  '/fabrication': {
    title: 'EVOLVE Fabrication',
    image: WelcomeScreenImages.BendCreator,
    description:
      'Fabrication brings conduit bends and cuts from Revit and EVOLVE’s fabrication tools into one workflow. Review fabrication items, prepare them for production, track cut and bend completion, and make work available to connected fabrication equipment.',
  },
  '/spooling': {
    title: 'Spooling',
    image: WelcomeScreenImages.Spooling,
    description:
      'Spooling lets your team review and work with Revit spools directly in the browser. Find spools in the model, assign them to Work Orders, and add fabrication information like tags and dimensions without needing to open Revit.',
  },
  '/model-browser': {
    title: 'Model Browser',
    image: WelcomeScreenImages.ModelBrowser,
    description:
      'Model Browser gives your team access to the project model directly in EVOLVE Shop. Navigate and inspect model elements in context to better understand the work being sent to the shop and where it fits in the project.',
  },
} as const satisfies Partial<
  Record<
    keyof typeof ALL_MODULE_ROUTES,
    {
      title: PageTitle;
      image: string;
      description: string;
    }
  >
>;

export type PageSpecificWelcomeScreenRoute = keyof typeof pageSpecificWelcomeScreens;
